import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { breakpoints, spacing } from '@naf/theme';
import { GridCol } from '@naf/grid';
import { Tag } from '@naf-simba/elements-tag';
import { Text } from '@naf/text';

const HorizontalPadding = css`
  padding-left: ${spacing.space48};
  padding-right: ${spacing.space48};

  @media (max-width: ${breakpoints.m}) {
    padding-left: ${spacing.space24};
    padding-right: ${spacing.space24};
  }
`;

const HeaderColorBox = styled.div`
  height: 275px;
  width: 100vw;
  position: absolute;
  left: 0;
  top: calc(-129px + 120px);
  z-index: -100;

  @media (max-width: ${breakpoints.m}) {
    top: calc(-129px + 80px);
    height: 259px;
  }

  @media (max-width: ${breakpoints.s}) {
    height: 232px;
  }
`;

const HeaderGridCol = styled(GridCol)`
  box-sizing: border-box;
  background: #fff;
  margin-top: ${spacing.space32};
  max-width: 736px;
  ${HorizontalPadding};

  @media (max-width: ${breakpoints.l}) {
    margin-top: ${spacing.space40};
  }

  @media (max-width: ${breakpoints.m}) {
    max-width: 579px;
    margin-top: ${spacing.space32};
  }

  @media (max-width: ${breakpoints.s}) {
    padding-left: ${spacing.space12};
    padding-right: ${spacing.space12};
    margin-left: -${spacing.space12};
    margin-right: -${spacing.space12};
  }

  p {
    margin: 0;
    margin-bottom: ${spacing.space8};
  }

  h1 {
    margin: ${spacing.space48} 0 0;

    @media (max-width: ${breakpoints.m}) {
      margin-top: ${spacing.space32};
    }
    @media (max-width: ${breakpoints.s}) {
      margin-top: ${spacing.space20};
    }
  }

  h2,
  h3 {
    margin: 0;
  }
`;

const BodyCol = styled(GridCol)`
  ${HorizontalPadding};
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  column-gap: ${spacing.space32};

  > * {
    grid-column: 1 / span 8;
    max-width: 100%;
  }

  @media (max-width: ${breakpoints.m}) {
    column-gap: ${spacing.space24};

    > * {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: ${breakpoints.s}) {
    padding: 0;
  }
`;

const CustomDisplayCol = styled(GridCol)`
  ${HorizontalPadding};
  padding-top: ${spacing.space16};

  @media (max-width: ${breakpoints.s}) {
    padding: 0;
  }
`;

const LogIn = styled.div`
  display: flex;
  flex-direction: column;
  ${HorizontalPadding};

  @media (max-width: ${breakpoints.s}) {
    padding: ${spacing.space24} 0 0;
  }

  p {
    margin: 0 0 ${spacing.space4};
  }
`;

const Body = styled.div`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: repeat(12, 1fr);
  column-gap: ${spacing.space32};

  > * {
    grid-column: 1 / span 8;
    max-width: 100%;
  }

  @media (max-width: ${breakpoints.m}) {
    column-gap: ${spacing.space24};

    > * {
      grid-column: 1 / -1;
    }
  }

  .CtaButton {
    margin-bottom: ${spacing.space32};
  }

  h2 {
    margin: ${spacing.space32} 0 ${spacing.space12};
    scroll-margin-top: 100px;
  }

  h3,
  p {
    margin: 0 0 ${spacing.space16};
  }

  /* The accordions render their header inside an h3 (headingLevel), which the rule above would
     otherwise give a 16px bottom margin — Simba's own reset on that heading is a single class. */
  .accordion > h3,
  .accordion p {
    margin: 0;
  }

  .accordion:last-of-type {
    margin-bottom: ${spacing.space32};
  }

  ul:not([id^='downshift']) {
    margin-bottom: ${spacing.space32};
  }

  form p {
    margin: 0;
  }

  form ~ button {
    margin-bottom: ${spacing.space32};
  }

  > div:first-child {
    display: flex;
    flex-direction: column;
  }

  .accordion + h3 {
    margin-top: ${spacing.space56};
    margin-bottom: ${spacing.space8};
  }

  h2 + h3 {
    margin-top: ${spacing.space24};
    margin-bottom: ${spacing.space8};
  }

  #videoWrapper {
    margin: 0;
    margin-bottom: ${spacing.space24};
  }
`;

const StyledTag = styled(Tag)`
  display: flex;
  width: fit-content;
  margin-bottom: ${spacing.space12};

  @media (max-width: ${breakpoints.s}) {
    margin-bottom: ${spacing.space8};
  }
`;

const TextWrapper = styled.div`
  display: flex;
  white-space: pre;
  align-items: center;

  &:first-child {
    margin-bottom: ${spacing.space4};
  }

  h2 {
    margin: 0;
  }
  h3 {
    margin: 0;
  }
`;

const InlineText = styled(Text)`
  display: inline-flex;
  white-space: pre;
`;

const Content = styled.div`
  width: fit-content;
  display: flex;
  flex-direction: column;
`;

const Ingress = styled(Text)`
  margin: 0;
  margin-bottom: ${spacing.space32};
`;

const ProductCol = styled(GridCol)`
  ${HorizontalPadding};
  margin-top: -${spacing.space16};

  a {
    margin-top: ${spacing.space32};
  }

  @media (max-width: ${breakpoints.s}) {
    padding: 0;
  }
`;

const QuoteCol = styled(GridCol)`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing.space64};
  ${HorizontalPadding};

  blockquote {
    width: 100%;
  }

  @media (max-width: ${breakpoints.m}) {
    margin-bottom: ${spacing.space48};
  }

  @media (max-width: ${breakpoints.s}) {
    margin-bottom: ${spacing.space40};
    padding: 0;
  }
`;

const RegisteredVehiclesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.space16};
  margin-top: ${spacing.space16};

  > div {
    flex: 1 1 calc(50% - ${spacing.space16});
    max-width: calc(50% - ${spacing.space16});
  }

  @media (max-width: ${breakpoints.l}) {
    flex-direction: column;

    > div {
      flex: 1 1 100%;
      max-width: 100%;
    }
  }
`;

export default {
  HeaderColorBox,
  HeaderGridCol,
  BodyCol,
  LogIn,
  Body,
  StyledTag,
  TextWrapper,
  InlineText,
  Content,
  Ingress,
  ProductCol,
  QuoteCol,
  CustomDisplayCol,
  RegisteredVehiclesContainer,
};
